import { IBox } from './exportLabelTypes'

interface IPoint {
  x: number
  y: number
}

export interface IAlongPathCandidate {
  centre: IPoint
  angleRad: number
  box: IBox
}

// Text never reads upside-down: the angle is held to (-90°, 90°].
export const uprightAngle = (angleRad: number): number => {
  if (angleRad > Math.PI / 2) return angleRad - Math.PI
  if (angleRad <= -Math.PI / 2) return angleRad + Math.PI
  return angleRad
}

// The upright box around a width × height rectangle centred on `centre` and turned by `angleRad`.
export function rotatedBox(
  centre: IPoint,
  width: number,
  height: number,
  angleRad: number,
): IBox {
  const cos = Math.abs(Math.cos(angleRad))
  const sin = Math.abs(Math.sin(angleRad))
  const halfWidth = (cos * width + sin * height) / 2
  const halfHeight = (sin * width + cos * height) / 2
  return {
    x: centre.x - halfWidth,
    y: centre.y - halfHeight,
    width: 2 * halfWidth,
    height: 2 * halfHeight,
  }
}

const signedArea = (path: IPoint[]): number =>
  path.reduce((sum, p, i) => {
    const q = path[(i + 1) % path.length]
    return sum + p.x * q.y - q.x * p.y
  }, 0) / 2

// Like a blueprint's annotation: the text runs along one of the geometry's own segments, longest
// first, set off it by `gapPx`. Inside a polygon first, then outside; above a line, then below.
export function alongPathCandidates(
  path: IPoint[],
  isClosed: boolean,
  size: { width: number; height: number },
  gapPx: number,
): IAlongPathCandidate[] {
  const count = isClosed ? path.length : path.length - 1
  // On a y-down page, a ring with positive shoelace area has its inside on (-dy, dx) of each edge.
  const inside = isClosed ? Math.sign(signedArea(path)) : 0
  const offset = gapPx + size.height / 2
  return Array.from({ length: Math.max(0, count) }, (_, i) => ({
    a: path[i],
    b: path[(i + 1) % path.length],
  }))
    .map(({ a, b }) => ({ a, b, length: Math.hypot(b.x - a.x, b.y - a.y) }))
    .filter(segment => segment.length >= size.width)
    .sort((s, t) => t.length - s.length)
    .flatMap(({ a, b, length }) => {
      const angleRad = uprightAngle(Math.atan2(b.y - a.y, b.x - a.x))
      const normal = inside
        ? {
            x: (-(b.y - a.y) / length) * inside,
            y: ((b.x - a.x) / length) * inside,
          }
        : { x: Math.sin(angleRad), y: -Math.cos(angleRad) }
      const middle = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }
      return [1, -1].map(side => {
        const centre = {
          x: middle.x + normal.x * offset * side,
          y: middle.y + normal.y * offset * side,
        }
        return {
          centre,
          angleRad,
          box: rotatedBox(centre, size.width, size.height, angleRad),
        }
      })
    })
}
